import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, XCircle, FileText, User, ChevronRight, ChevronLeft, Award, Clock } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QCMPublicPage = () => {
  const [searchParams] = useSearchParams();
  const qcmId = searchParams.get('qcm');
  const dossierId = searchParams.get('dossier');
  const dateJournee = searchParams.get('date') || '';

  const [step, setStep] = useState('select_candidat'); // select_candidat, passage, result
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qcmData, setCqmData] = useState(null);
  const [candidats, setCandidats] = useState([]);
  const [dossierIntitule, setDossierIntitule] = useState('');
  const [selectedCandidat, setSelectedCandidat] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timer, setTimer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  const fetchQcm = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/qcms/${qcmId}/public-passage?dossier_id=${dossierId}&date_journee=${dateJournee}`);
      setCqmData(res.data.qcm);
      setCandidats(res.data.candidats);
      setDossierIntitule(res.data.dossier_intitule);
    } catch (err) {
      setError(err.response?.data?.detail || 'QCM introuvable ou non disponible');
    }
    setLoading(false);
  }, [qcmId, dossierId, dateJournee]);

  useEffect(() => {
    if (!qcmId || !dossierId) {
      setError('Lien invalide. Paramètres manquants.');
      setLoading(false);
      return;
    }
    fetchQcm();
  }, [qcmId, dossierId, fetchQcm]);

  const startPassage = (candidat) => {
    setSelectedCandidat(candidat);
    setStep('passage');
    setCurrentQuestion(0);
    setAnswers({});
    if (qcmData.settings?.temps_limite_minutes) {
      setTimeLeft(qcmData.settings.temps_limite_minutes * 60);
    }
  };

  const selectOption = (questionId, optionId, type) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (type === 'choix_unique' || type === 'vrai_faux') {
        return { ...prev, [questionId]: [optionId] };
      }
      // choix_multiple
      if (current.includes(optionId)) {
        return { ...prev, [questionId]: current.filter(id => id !== optionId) };
      }
      return { ...prev, [questionId]: [...current, optionId] };
    });
  };

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/qcms/${qcmId}/submit-public`, {
        candidat_id: selectedCandidat.id,
        dossier_id: dossierId,
        date_journee: dateJournee,
        reponses: answers,
      });
      setResult(res.data);
      setStep('result');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la soumission');
    }
    setSubmitting(false);
  }, [qcmId, dossierId, dateJournee, selectedCandidat, answers]);

  // Timer
  useEffect(() => {
    if (timeLeft === null || step !== 'passage') return;
    if (timeLeft <= 0) { handleSubmit(); return; }
    const interval = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timeLeft, step, handleSubmit]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Chargement du QCM...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full border-0 shadow-lg">
          <CardContent className="py-12 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-800">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 1: Select candidat
  if (step === 'select_candidat') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-slate-50 py-8 px-4" data-testid="qcm-public-select">
        <div className="max-w-lg mx-auto space-y-6">
          <div className="text-center">
            <div className="w-14 h-14 rounded-xl bg-indigo-600 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">{qcmData.titre}</h1>
            {qcmData.description && <p className="text-sm text-slate-500 mt-1">{qcmData.description}</p>}
            <p className="text-xs text-slate-400 mt-2">{dossierIntitule}</p>
            <div className="flex items-center justify-center gap-3 mt-3 text-xs text-slate-500">
              <span>{qcmData.questions.length} questions</span>
              {qcmData.settings?.temps_limite_minutes && (
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {qcmData.settings.temps_limite_minutes} min</span>
              )}
            </div>
          </div>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600">Sélectionnez votre nom :</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {candidats.map(c => (
                <button
                  key={c.id}
                  disabled={c.deja_passe}
                  onClick={() => startPassage(c)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                    c.deja_passe
                      ? 'bg-emerald-50 cursor-not-allowed'
                      : 'hover:bg-indigo-50 cursor-pointer border border-slate-200 hover:border-indigo-300'
                  }`}
                  data-testid={`candidat-select-${c.id}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                    c.deja_passe ? 'bg-emerald-200 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                  }`}>
                    {c.prenom?.[0]}{c.nom?.[0]}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${c.deja_passe ? 'text-emerald-700' : 'text-slate-800'}`}>
                      {c.nom} {c.prenom}
                    </p>
                  </div>
                  {c.deja_passe ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Terminé{c.nb_tentatives > 1 ? ` · ${c.nb_tentatives} tentatives` : ''}</Badge>
                  ) : c.repassage_autorise ? (
                    <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`qcm-public-repassage-${c.id}`}>Nouvelle tentative (n°{(c.nb_tentatives || 1) + 1})</Badge>
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  )}
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Step 2: Passage
  if (step === 'passage') {
    const q = qcmData.questions[currentQuestion];
    const selectedIds = answers[q.id] || [];
    const answered = Object.keys(answers).length;

    return (
      <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="qcm-public-passage">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-800">{qcmData.titre}</p>
              <p className="text-xs text-slate-400">{selectedCandidat.nom} {selectedCandidat.prenom}</p>
            </div>
            <div className="flex items-center gap-3">
              {timeLeft !== null && (
                <span className={`text-sm font-mono font-bold px-3 py-1 rounded-lg ${
                  timeLeft < 60 ? 'bg-red-100 text-red-700' : timeLeft < 300 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  <Clock className="w-3 h-3 inline mr-1" />{formatTime(timeLeft)}
                </span>
              )}
              <span className="text-xs text-slate-500">{answered}/{qcmData.questions.length}</span>
            </div>
          </div>

          {/* Progress */}
          <div className="w-full bg-slate-200 rounded-full h-1.5">
            <div className="bg-indigo-600 h-1.5 rounded-full transition-all" style={{ width: `${((currentQuestion + 1) / qcmData.questions.length) * 100}%` }} />
          </div>

          {/* Question */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-start gap-3">
                <span className="text-xs font-bold text-indigo-600 bg-indigo-50 rounded-lg px-2 py-1 flex-shrink-0">
                  Q{currentQuestion + 1}
                </span>
                <p className="text-base font-medium text-slate-800 leading-relaxed">{q.texte}</p>
                {q.image_id && (
                  <img
                    src={`${API_URL}/qcm-images/${q.image_id}`}
                    alt="Illustration de la question"
                    className="max-h-64 rounded-lg border border-slate-200 mt-2"
                    data-testid={`question-image-${q.id}`}
                  />
                )}
              </div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wide">
                {q.type === 'choix_multiple' ? 'Plusieurs réponses possibles' : 'Une seule réponse'}
              </p>
              <div className="space-y-2">
                {q.options.map(o => {
                  const isSelected = selectedIds.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      className={`w-full text-left p-3.5 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                          : 'border-slate-200 hover:border-slate-300 text-slate-700'
                      }`}
                      onClick={() => selectOption(q.id, o.id, q.type)}
                      data-testid={`option-${o.id}`}
                    >
                      <span className="text-sm">{o.texte}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
              disabled={currentQuestion === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
            </Button>

            {currentQuestion < qcmData.questions.length - 1 ? (
              <Button
                size="sm"
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
              >
                Suivant <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-submit-qcm"
              >
                {submitting ? 'Envoi...' : 'Terminer et envoyer'}
              </Button>
            )}
          </div>

          {/* Question dots */}
          <div className="flex flex-wrap gap-1.5 justify-center">
            {qcmData.questions.map((qq, i) => (
              <button
                key={qq.id}
                onClick={() => setCurrentQuestion(i)}
                className={`w-7 h-7 rounded-lg text-[10px] font-bold transition-all ${
                  i === currentQuestion ? 'bg-indigo-600 text-white' :
                  answers[qq.id] ? 'bg-indigo-100 text-indigo-700' :
                  'bg-slate-100 text-slate-400'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Result
  if (step === 'result') {
    const passed = result.reussi;
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4" data-testid="qcm-public-result">
        <div className="max-w-md mx-auto text-center space-y-6">
          <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${
            passed === true ? 'bg-emerald-100' : passed === false ? 'bg-red-100' : 'bg-indigo-100'
          }`}>
            {passed === true ? <Award className="w-10 h-10 text-emerald-600" /> :
             passed === false ? <XCircle className="w-10 h-10 text-red-500" /> :
             <CheckCircle2 className="w-10 h-10 text-indigo-600" />}
          </div>

          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {passed === true ? 'Bravo !' : passed === false ? 'Dommage...' : 'QCM terminé'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{selectedCandidat.nom} {selectedCandidat.prenom}</p>
          </div>

          <Card className="border-0 shadow-sm">
            <CardContent className="py-6">
              <p className={`text-5xl font-bold ${
                result.pourcentage >= 70 ? 'text-emerald-600' : result.pourcentage >= 50 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {result.pourcentage}%
              </p>
              <p className="text-sm text-slate-500 mt-2">
                {result.score} / {result.score_max} points
              </p>
              {passed === true && <p className="text-sm font-medium text-emerald-600 mt-2">Evaluation réussie</p>}
              {passed === false && <p className="text-sm font-medium text-red-600 mt-2">Seuil non atteint</p>}
            </CardContent>
          </Card>

          <p className="text-xs text-slate-400">Vous pouvez fermer cette page. Vos résultats ont été enregistrés.</p>
        </div>
      </div>
    );
  }

  return null;
};

export default QCMPublicPage;
